import { CommonEditLogic } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/common-edit-logic';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import type { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import type ChartManager from '@src/slide/application/service/chart-manager';
import { SubChartModelObserver } from '@src/slide/application/service/modelObserver/chartObserver';
import { reaction } from 'mobx';
import { IDisposer } from 'mobx-utils';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import {
    ITextBodyStyle,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { PropertyEditorShowAction } from '@src/js/constant';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { ChartElementType, IChartElement, IChartElementGroup } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import { SimpleBBox } from '@tencent/chart/lib/antv/g-base';
import { ISelectElement } from '@tencent/chart/lib/chart-ui/interface';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';

export class ChartEditLogic extends CommonEditLogic {
    public chartManager: ChartManager;
    public subChartChangeSetObservers: SubChartModelObserver[];
    public selectDisposer: IDisposer;
    public editStateDisposer: IDisposer;
    public dragDisposer: IDisposer;
    public selectionArrDisposer: IDisposer;
    declare protected factoryOptions: InterfaceFactoryOptions<IChartModel>;

    public constructor(factoryOptions: InterfaceFactoryOptions<IChartModel>, component: ComponentFactory) {
        super(factoryOptions, component);
        this.chartManager = (this.getAppServices() as IPcEditorAppServices).editableServiceLayer!.chartManager;
        this.component.inner.onDoubleClick(this.handleInnerDoubleClick);
        this.addInteractionObserver();
    }

    public dispose(): void {
        super.dispose();
        this.selectDisposer?.();
        this.editStateDisposer?.();
        this.dragDisposer?.();
        this.selectionArrDisposer?.();
    }

    public handleInnerDragStart = (e: MouseEvent) => {
        e?.stopPropagation?.();
        // 当元素被拖拽时，需要也选中图表本身
        if (!this.factoryOptions.model.selected) {
            this.selectElement(this.factoryOptions.model);
        }

        this.chartModel.setDraggingInnerState(true);
    };

    /**
     * 处理图表内部元素拖拽
     */
    public handleInnerDrag = (e: MouseEvent) => {
        e?.stopPropagation?.();
        this.chartModel.setDraggingInnerState(true);
    };

    /**
     * 处理图表内部元素拖拽结束
     */
    public handleInnerDragEnd = (e: MouseEvent) => {
        this.chartModel.setDraggingInnerState(false);
    };

    public handleElementLayoutChange = (el: ISelectElement, rect: SimpleBBox, diff: SimpleBBox) => {
        this.chartModel.chartProperty.updateSelectElementLayout(
            this.convertSimpleBBoxToRect(rect),
            this.convertSimpleBBoxToRect(diff),
        );
    };

    /**
     * 图表标题鼠标按下事件
     */
    public handleChartTitleMouseDown = (e) => {
        if (!this.checkCurrentEditable() || this.isMultiSelected()) {
            return;
        }

        const titleBounds = e.target?.localBounds;

        if (titleBounds) {
            // 先清空图表库的选中态
            this.chartComponent.resetSelectState();

            this.markElementSelected(this.titleElement);

            this.chartTitleComponent.updateTextEditor();
        }
    };

    // 文本更变了，需要落盘。
    public handleTitleChange(value: { paragraphList: IParagraph[], textBodyStyle: ITextBodyStyle }) {
        if (!this.checkCurrentEditable()) {
            return;
        }

        const { paragraphList } = value;

        const chartTitleProperty = this.chartModel.chartProperty.chartTitle;
        if (chartTitleProperty) {
            if (this.isTitleEmpty(paragraphList)) {
                chartTitleProperty.removeTitle();
            } else {
                chartTitleProperty.updateProps({ text: { paragraphList } });
            }
        }

        // 在文字改变后，需要同步更新selectElement中的rect
        if (this.titleTextEditor) {
            this.updateSelectedElement(this.titleElement);
        }
    }

    // 文本输入变更，非落盘逻辑处理，这里处理选中边框变化
    public handleTitleTextChange() {
        // 在文字改变后，需要同步更新selectElement中的rect
        if (this.titleTextEditor) {
            this.updateSelectedElement(this.titleElement);
        }
    }

    public updateSelectedTitleElement() {
        this.updateSelectedElement(this.titleElement);
    }


    /**
     * 处理图表元素点击
     * @param selectGeometry 选中的元素
     * @param isSingleClick 是否是长间隔点击。如果选中一个元素，且该元素处于一个group中。短间隔的点击，会选中整组元素
     *  如果是"单击"，且该元素group处于整体选中的情况，会取消整组的选中，单独选中该元素。
     *  单击：非元素双击，双击的间隔大于一定时间
     */
    public handleElementSelected(selectTarget: IChartElement, isSingleClick = false) {
        if (!this.checkCurrentEditable()) {
            return;
        }

        const chartElements = this.chartComponent.chartElements as Array<IChartElementGroup | IChartElement>;

        const isElementReadySelected = this.chartModel.isElementSelected(selectTarget.id);
        if (isElementReadySelected) {
            return;
        }

        const isElementInGroup = !!(selectTarget as IChartElement).groupId && !selectTarget.disableGroupSelect;
        if (isElementInGroup) {
            const group = chartElements.find(ele => ele.id === selectTarget.groupId);

            const isGroupAllSelected = this.chartModel.isElementSelected((group as IChartElementGroup)?.id);
            if (isGroupAllSelected && isSingleClick && !selectTarget.disableSingleSelect) {
                // 单独选中元素
                this.markElementSelected(selectTarget);
            } else {
                const hasElementAlreadySelectedInGroup = (this.chartModel
                    .selectedElement as IChartElement)?.groupId === selectTarget.groupId;

                // 如果同组有选中的元素，就在单独选中target元素
                if (hasElementAlreadySelectedInGroup) {
                    this.markElementSelected(selectTarget);
                } else {
                    // 整组整体选中，万一没找到group，兜底选中该元素
                    this.markElementSelected(group || selectTarget);
                }
            }
        } else {
            this.markElementSelected(selectTarget);
        }

        this.triggerTitleBlur();
    }

    // 处理空白区域的点击（如果点到title、hitArea上面是不会出发到这里的mouseDown），所以这里点击就是要取消选中
    public handlePlotBlankMouseDown() {
        this.chartModel.removeAllSelectedElements();

        this.triggerTitleBlur();
    }

    // 在图表重新绘制时，如果有选中态的元素，更新它的select-border
    public updateSelectedElementStyle() {
        const oldSelectedElement = this.chartModel.selectedElement;

        // 标题这里暂时不处理，因为和title不被收集在chartComponent.chartElements中，由title-component自己处理
        if (oldSelectedElement && oldSelectedElement.type !== ChartElementType.TITLE) {
            let newChartElement: IChartElement | IChartElementGroup | undefined = undefined;
            this.chartComponent.chartElements?.map((ele) => {
                if (ele.id === oldSelectedElement.id) {
                    newChartElement = ele;
                } else if (ele.group) {
                    // 如果是group，需要往里找一层
                    const element = ele.group.find(groupEle => groupEle.id === oldSelectedElement.id);
                    if (element) {
                        newChartElement = element;
                    }
                }
            });

            if (newChartElement) {
                this.updateSelectedElement(newChartElement);
            } else {
                this.chartModel.removeAllSelectedElements();
            }
        }
    }

    // todo 这里这么写有点太泛了，容易逻辑割裂，以为在update的时候只干了这个。命名需要重新定一下，以及下面的onChartSourceUpdate
    public onTitleEditorUpdate() {
        // 如果处理选中态，需要更新选种样式边框等
        this.updateSelectedTitleElement();
    }

    // 当图表G2重新渲染时需要处理的逻辑
    public onChartSourceUpdate() {
        // 更新已经选中情况
        this.updateSelectedElementStyle();
    }

    public getChartHitAreaProps(chartElements: Array<IChartElement | IChartElementGroup>) {
        const hitAreaPropList: any[] = [];

        chartElements.forEach((ele) => {
            if (this.isChartElementGroup(ele)) {
                const elements = ele.group;

                elements.forEach((element) => {
                    hitAreaPropList.push(this.generateHitAreaProp(element));
                });
            } else {
                hitAreaPropList.push(this.generateHitAreaProp(ele));
            }
        });

        return hitAreaPropList;
    }

    public triggerAllElementUnSelected() {
        this.triggerTitleBlur();
        this.chartModel.removeAllSelectedElements();
        this.chartComponent.resetSelectState();
    }

    public triggerTitleBlur() {
        this.chartModel.removeIfSelected(ChartElementType.TITLE);
        this.titleTextEditor?.triggerBlur();
        this.chartTitleComponent?.updateTextEditor();
    }

    // 处理非标题区域的点击事件(点到标题不会触发这里)，这里目的是取消标题选中
    public handleChartInstanceMouseDown() {
        this.triggerTitleBlur();
    }

    public isMultiSelected() {
        const { selectedArray } = this.getSelectionService().selectedElements;
        return selectedArray && selectedArray?.length > 1;
    }

    private isChartElementGroup(ele: IChartElementGroup | IChartElement): ele is IChartElementGroup {
        return !!(ele as IChartElementGroup).group;
    }

    private generateHitAreaProp(ele: IChartElement) {
        return {
            // 对超出图表画布元素的path进行截取， 否则超出部分的hitArea导致slide画布居中和缩放问题
            path: ele.path?.map(command => command.map(item => (isNaN(Number(item)) ? item : Math.max(0, Number(item)))).join(' ')).join(' '),
            factoryOptions: this.factoryOptions,
            element: ele,
        };
    }

    private addInteractionObserver() {
        if (this.factoryOptions.fromScale === 0) {  // 注意： 编辑页才执行
            // 图表这里的逻辑应该是：
            // 1. 如果model.selected 为false，整体失焦，触发所有失焦的事件，包括编辑保存等
            // 2. 如果model.selected 为true，需要根据鼠标的坐标，进行部分元素的失焦判定，同时需要维护chartModel.selectedElementArray
            this.selectDisposer = reaction(() => ({
                selected: this.chartModel?.selected,
            }), (obj) => {
                if (obj.selected) {
                    // 聚焦
                } else {
                    // 失焦
                    this.triggerAllElementUnSelected();
                }
            });

            this.editStateDisposer = reaction(() => ({
                isResizing: this.getEditingStateManager().isResizing,
                isDragging: this.getEditingStateManager().isDragging,
            }), ({ isResizing, isDragging }) => {
                if (isResizing || isDragging) {
                    this.handleInEditingState();
                }
            });

            // 多选的时候清除元素选中
            this.selectionArrDisposer = reaction(() => ({
                selectedElementArray: this.getSelectionService().selectedElements.selectedArray,
            }), ({ selectedElementArray }) => {
                if (selectedElementArray && selectedElementArray.length > 1 && this.chartModel.selected) {
                    this.chartModel.removeAllSelectedElements();
                    this.chartComponent.resetSelectState();
                }
            });
        }
    }

    private handleInEditingState() {
        this.chartModel.removeAllSelectedElements();
        this.chartComponent.resetSelectState();
    }

    private markElementSelected(el: IChartElement | IChartElementGroup) {
        const isElementAlreadySelected = this.chartModel.isElementSelected(el.id);

        if (!isElementAlreadySelected) {
            this.chartModel.markSelected(el);
        }
    }

    // 这个update有别于markElementSelected。这里侧重点是"直接更新选中的元素"，从A到新A，而不是做"选中为标记"，从A到B
    private updateSelectedElement(el: IChartElement | IChartElementGroup) {
        const isElementAlreadySelected = this.chartModel.isElementSelected(el.id);

        if (isElementAlreadySelected) {
            this.chartModel.markSelected(el);
        }
    }

    private get titleElement(): IChartElement {
        return {
            rect: this.titleTextRect,
            // TODO 临时这么写一下，暂时用不到
            path: [[]],
            type: ChartElementType.TITLE,
            id: ChartElementType.TITLE,
        };
    }

    private get titleTextRect() {
        return this.chartTitleComponent.getRealTitleRect();
    }

    private get titleTextEditor() {
        return this.chartTitleComponent?.textEditor;
    }

    private get chartTitleComponent() {
        return this.component.inner.chartTitleComponent;
    }

    public get chartComponent() {
        return this.component.inner.chartComponent;
    }

    private get chartModel(): IChartModel {
        return this.factoryOptions.model;
    }

    private handleInnerDoubleClick = () => {
        // 没有实际渲染的图表不支持编辑，没有编辑权限的双击也不出侧边栏
        if (!this.component.inner.isActualRenderChart || !this.checkCurrentEditable()) return;
        this.getRightSideBarManager()?.setPropertyEditorShowAction(PropertyEditorShowAction.DOUBLE_CLICK_TOOGLE);
        this.getRightSideBarServices().hide();
    };

    private getRightSideBarManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .editableServiceLayer?.rightSideBarManager;
    }
    private getEditingStateManager() {
        return this.getAppServices().viewableServiceLayer.editingStateManager;
    }

    private getRightSideBarServices() {
        return this.getAppServices().viewableServiceLayer.rightSideBarService;
    }

    private checkCurrentEditable(): boolean {
        const { model } = this.factoryOptions;
        return model.currentEditState === PPTEditorStateType.EDITABLE;
    }

    private isTitleEmpty(paragraphList: IParagraph[]): boolean {
        if (!paragraphList) {
            return true;
        }

        if (paragraphList.length > 1) {
            return false;
        }

        const textRuns = paragraphList[0]?.textRuns;
        return !textRuns || textRuns.length === 0;
    }

    private convertSimpleBBoxToRect(box: SimpleBBox): IRect {
        return {
            left: box.x,
            top: box.y,
            width: box.width,
            height: box.height,
        };
    }
}
